import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import { addMinutes, format } from 'date-fns'
import { useEffect, useState } from 'react'
import { Line } from 'react-chartjs-2'

// import ArrowDown from '../../../assets/images/icons/arrow-down.svg?react'
// import ArrowUp from '../../../assets/images/icons/arrow-up.svg?react'
import IndexIcon from '../../../assets/images/icons/index-icon.svg?react'
import { useSidebarIndexQuery } from '../../../store/api'
import type { Dataset, IndexProps } from '../../../store/types/dashboard'
import SpinLoader from '../../../utils/loaders/spin'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
)

function IndexChart() {
  const { data, isLoading } = useSidebarIndexQuery()

  const [indexData, setIndexData] = useState<IndexProps>({
    index: '',
    dataset: [],
    average: '',
  })

  useEffect(() => {
    if (data) {
      setIndexData(data)
    }
  }, [data])

  return (
    <>
      {isLoading ? (
        <LoadingChart />
      ) : (
        <Chart
          average={indexData.average}
          dataset={indexData.dataset}
          index={indexData.index}
        />
      )}
    </>
  )
}

export default IndexChart

function LoadingChart() {
  return (
    <div className="flex container mx-auto p-6 flex-col sm:col-span-5 border-2 border-solid border-gray-200 rounded-lg shadow">
      <div className="flex flex-col justify-between">
        <div className="flex flex-row items-center pb-2.5">
          <IndexIcon />
          <span className="pl-3 font-semibold	text-2xl text-gray-900">
            Index
          </span>
        </div>
      </div>
      <div className="flex flex-col items-center">
        <SpinLoader />
        <div className="font-medium text-sm text-gray-700 pb-1 pt-4">
          Calculating...
        </div>
        <p className="font-normal text-xs text-gray-500">
          Calculation will be completed within 20 minutes.
        </p>
      </div>
    </div>
  )
}

interface DataItem {
  index_date: string
  prisync_index: string
}

function Chart({ average, dataset, index }: IndexProps) {
  const sortedData = [...dataset].sort(
    (a: Dataset, b: Dataset) =>
      new Date(a.index_date).getTime() - new Date(b.index_date).getTime(),
  )
  const labels =
    sortedData.map((item) =>
      item.index_date
        ? format(
            addMinutes(
              new Date(item.index_date),
              new Date(item.index_date).getTimezoneOffset(),
            ),
            'dd/LL/yyyy HH:mm',
          )
        : '',
    ) ?? []
  const prisyncIndex: number[] =
    sortedData.map((item: DataItem) => parseFloat(item.prisync_index)) ?? []

  const chartIndex = parseFloat(index === null ? '' : index)
  const chartAverage = parseFloat(average === null ? '' : average)

  const d = {
    labels: labels,
    datasets: [
      {
        label: 'Index',
        data: prisyncIndex,
        fill: true,
        borderColor: '#12B76A',
        backgroundColor: '#ECFDF3',
        tension: 0.2,
        borderWidth: 3.95312,
        pointRadius: 0,
      },
    ],
  }
  return (
    <div className="sm:col-span-5 border border-solid border-gray-200 rounded-lg shadow lg:w-full">
      <div className="px-4 py-5 lg:p-6 flex flex-col lg:flex-row h-full">
        <div className="flex flex-col justify-between h-full lg:w-1/3">
          <div className="flex flex-row gap-2">
            <div className="flex">
              <IndexIcon />
            </div>
            <span className="font-semibold	text-2xl text-gray-900">Index</span>
          </div>
          <div className="flex flex-row mt-8 lg:mt-0 gap-x-6 lg:gap-x-0">
            <div className="flex flex-col justify-center items-center">
              <div className="font-semibold text-3xl text-gray-900 min-w-[130px]">
                <label className="font-semibold text-3xl text-gray-900 block text-center">
                  {index}
                </label>
              </div>
              {/* 
              {chartAverage > chartIndex ? (
                <div className="flex flex-row mt-4">
                  <div className="flex">
                    <ArrowDown />
                  </div>

                  <span className="pl-2 font-medium text-sm text-red-500">
                    {(
                      ((chartAverage - chartIndex) / chartAverage) *
                      100
                    ).toFixed(0)}
                    %
                  </span>
                  <span className="pl-3 font-medium text-sm text-gray-500 whitespace-nowrap ">
                    lower than av.
                  </span>
                </div>
              ) : (
                <div className="flex flex-row mt-4">
                  <div className="flex">
                    <ArrowUp />
                  </div>

                  <span className="pl-2 font-medium text-sm text-success-700">
                    {(((chartIndex - chartAverage) / chartIndex) * 100).toFixed(
                      0,
                    )}
                    %
                  </span>
                  <span className="pl-3 font-medium text-sm text-gray-500 whitespace-nowrap">
                    higher than av.
                  </span>
                </div>
              )}
              */}
            </div>
            <div className="lg:pl-10 w-36 lg:w-40 xl:w-60 xxl:w-72">
              <Line
                options={{
                  interaction: {
                    mode: 'index',
                    intersect: false,
                  },
                  plugins: {
                    filler: {
                      propagate: false,
                    },
                    legend: {
                      display: false,
                    },
                    title: {
                      display: false,
                    },
                    datalabels: {
                      display: false,
                    },
                  },
                  scales: {
                    x: {
                      ticks: {
                        display: false,
                      },
                      border: { display: false },
                      grid: {
                        display: false,
                        drawOnChartArea: false,
                        drawTicks: false,
                      },
                    },
                    y: {
                      ticks: {
                        display: false,
                      },
                      border: { display: false },
                      grid: {
                        display: false,
                        drawOnChartArea: false,
                        drawTicks: false,
                      },
                    },
                  },
                }}
                data={d}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
